import { useState, useCallback, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  Heart, Wallet, Gift,
  Gem, Sparkles, Link2, Circle,
  Shirt, ShoppingBag, Scissors, Flower2,
  Lamp, Sofa, Utensils, Image as ImageIcon,
  Droplets, Leaf, Bath, FlaskConical,
  ToyBrick, Baby, Backpack, PartyPopper,
  User, UserRound, Briefcase,
  Search, ShoppingCart, PackageCheck,
  Store, Camera, Megaphone,
  type LucideIcon,
} from "lucide-react";
import hero from "@/assets/kupish/hero.webp";
import heroMobile from "@/assets/kupish/hero-mobile.webp";
import logoAsset from "@/assets/kupish-logo.png.asset.json";
import ogAsset from "@/assets/og-naslovnica-stran-v2.jpg.asset.json";

const ogImage = ogAsset.url;

import { categoryCovers } from "@/data/categoryImages";
import { listFeatured } from "@/lib/catalog.functions";
import { ProductCard } from "@/components/ProductCard";
import { ArticleCarousel } from "@/components/ArticleCarousel";
import { getArticlesBySection, getArticle } from "@/data/articles";
import { articleImageAlts } from "@/data/articleImages";


const PRESENTATION_SLUGS = [
  "priprava-izdelka-za-splet",
  "optimizacija-fotografij-izdelka",
  "optimizacija-besedila",
  "seo-osnove-za-trgovce",
  "marketing-za-spletne-trgovine",
  "marketing-za-trgovce",
] as const;

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "kupish.si — spletno nakupovalno središče novih izdelkov" },
      { name: "description", content: "Spletno nakupovalno središče kupish.si združuje nove izdelke preverjenih ponudnikov: elektronika, dom in vrt, avto, moda, šport in drogerija na enem mestu." },
      { property: "og:title", content: "kupish.si — spletno nakupovalno središče novih izdelkov" },
      { property: "og:description", content: "Več trgovin, en nakup. Odkrijte nove izdelke preverjenih slovenskih ponudnikov na kupish.si." },
      { property: "og:url", content: "https://kupish.si/" },
      { property: "og:image", content: `https://kupish.si${ogImage}` },
      { property: "og:image:secure_url", content: `https://kupish.si${ogImage}` },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "kupish.si — spletno nakupovalno središče novih izdelkov" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "kupish.si — spletno nakupovalno središče novih izdelkov" },
      { name: "twitter:description", content: "Več trgovin, en nakup. Odkrijte nove izdelke preverjenih slovenskih ponudnikov na kupish.si." },
      { name: "twitter:image", content: `https://kupish.si${ogImage}` },
      { name: "twitter:image:alt", content: "kupish.si — spletno nakupovalno središče novih izdelkov" },
    ],
    links: [
      { rel: "canonical", href: "https://kupish.si/" },
      { rel: "preload", as: "image", href: heroMobile, fetchpriority: "high", media: "(max-width: 767px)", type: "image/webp" },
      { rel: "preload", as: "image", href: hero, fetchpriority: "high", media: "(min-width: 768px)", type: "image/webp" },
    ],
  }),

  component: Index,
});

type SubLink = { name: string; slug: string; Icon: LucideIcon };
type TopCategory = {
  name: string;
  slug: string;
  image_url: string;
  description: string;
  subcategories: SubLink[];
};

const topCategories: TopCategory[] = [
  {
    name: "Drogerija",
    slug: "drogerija",
    image_url: categoryCovers["drogerija"],
    description: "Čistila, pripomočki in nega doma za vsakdanjo uporabo.",
    subcategories: [
      { name: "Nega doma", slug: "nega-doma", Icon: Bath },
      { name: "Čistila", slug: "cistila", Icon: FlaskConical },
      { name: "Krpe za čiščenje", slug: "krpe-za-ciscenje", Icon: Sparkles },
      { name: "Osebna nega", slug: "osebna-nega", Icon: Heart },
    ],
  },
  {
    name: "Elektronika & avdio",
    slug: "elektronika-avdio",
    image_url: categoryCovers["elektronika-avdio"],
    description: "Zvočniki, slušalke in zabavna elektronika preverjenih ponudnikov.",
    subcategories: [
      { name: "Avdio & video", slug: "avdio-video", Icon: ImageIcon },
      { name: "Zabavna elektronika", slug: "zabavna-elektronika", Icon: ToyBrick },
      { name: "Slušalke", slug: "slusalke", Icon: Circle },
      { name: "Telefonija", slug: "telefonija", Icon: Link2 },
    ],
  },
  {
    name: "Avto elektronika",
    slug: "avto-elektronika",
    image_url: categoryCovers["avto-elektronika"],
    description: "Avto-kamere, radii in prostoročno telefoniranje za vsako vožnjo.",
    subcategories: [
      { name: "Avto-kamere", slug: "avto-kamere", Icon: Camera },
      { name: "Prostoročno telefoniranje", slug: "prostorocno-telefoniranje", Icon: Link2 },
      { name: "FM radiji", slug: "fm-radiji", Icon: Megaphone },
    ],
  },
  {
    name: "Avtomobilska oprema",
    slug: "avtomobilska-oprema",
    image_url: categoryCovers["avtomobilska-oprema"],
    description: "Polnilci, nosilci in praktična oprema za vaš avtomobil.",
    subcategories: [
      { name: "Napajalniki in polnilci", slug: "napajlniki-in-polnilci", Icon: Wallet },
      { name: "Nosilci in držala", slug: "nosilci-in-drzala", Icon: Backpack },
    ],
  },
  {
    name: "Dom & vrt",
    slug: "dom-vrt",
    image_url: categoryCovers["dom-vrt"],
    description: "Izdelki za kuhinjo, dom in vrt, ki olajšajo vsakdan.",
    subcategories: [
      { name: "Vrt", slug: "vrt", Icon: Flower2 },
      { name: "Kuhinja", slug: "kuhinja", Icon: Utensils },
      { name: "Prti", slug: "prti", Icon: Lamp },
      { name: "Stojala", slug: "stojala", Icon: ShoppingBag },
    ],
  },
  {
    name: "Moda & dodatki",
    slug: "moda-dodatki",
    image_url: categoryCovers["moda-dodatki"],
    description: "Modni dodatki in nakit za vsak dan in posebne priložnosti.",
    subcategories: [
      { name: "Nakit", slug: "nakit", Icon: Gem },
    ],
  },
];


const EXCLUDED_VENDOR_SLUGS = new Set(["mglittery"]);

function FeaturedProducts() {
  const fetchFeatured = useServerFn(listFeatured);
  // Nov naključni paket ob vsakem obisku strani.
  const [seed] = useState(() => Math.random().toString(36).slice(2));
  const { data, isLoading } = useQuery({
    queryKey: ["featured-products", seed],
    queryFn: () => fetchFeatured({ data: { limit: 48 } }),
    staleTime: 0,
    gcTime: 0,
  });


  const [failed, setFailed] = useState<Set<string>>(new Set());
  const onUnavailable = useCallback((id: string) => {
    setFailed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const allProducts = data?.products ?? [];
  const candidates = useMemo(
    () =>
      allProducts.filter((p) => {
        const slug = (p as unknown as { vendor?: { slug?: string } }).vendor?.slug;
        if (slug && EXCLUDED_VENDOR_SLUGS.has(slug)) return false;
        if (failed.has((p as { id: string }).id)) return false;
        return true;
      }),
    [allProducts, failed],
  );
  const products = candidates.slice(0, 8);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-square bg-muted" />
            <div className="mt-4 h-4 bg-muted rounded w-3/4 mx-auto" />
            <div className="mt-2 h-3 bg-muted rounded w-1/2 mx-auto" />
          </div>
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center text-sm text-muted-foreground py-8">
        Trenutno ni nobenih izdelkov.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
      {products.map((product) => (
        <ProductCard
          key={product.id}
          product={product as unknown as React.ComponentProps<typeof ProductCard>["product"]}
          onUnavailable={onUnavailable}
        />
      ))}
    </div>
  );
}


function BrandDivider() {
  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 flex items-center justify-center py-4 md:py-8">
      <div className="flex-1 h-px bg-border" />
      <img
        src="/favicon.png"
        alt=""
        aria-hidden="true"
        width={24}
        height={24}
        loading="lazy"
        decoding="async"
        className="mx-4 h-6 w-6 shrink-0 rounded-full"
      />
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

function Index() {

  return (
    <>
      <section className="relative">
        <div className="w-full pb-2 md:pb-3">
          <div className="relative w-full overflow-hidden bg-muted">
            <picture>
              <source media="(max-width: 767px)" srcSet={heroMobile} type="image/webp" width={800} height={426} />
              <source media="(min-width: 768px)" srcSet={hero} type="image/webp" width={1600} height={853} />
              <img
                src={heroMobile}
                alt="kupish.si — spletno trgovsko središče z novimi izdelki različnih ponudnikov"
                width={800}
                height={426}
                fetchPriority="high"
                decoding="async"
                sizes="100vw"
                className="block w-full h-[310px] sm:h-[400px] md:h-auto object-cover md:blur-[3px]"
              />
            </picture>

            <div className="absolute inset-0 bg-background/55 md:bg-background/10 md:backdrop-blur-0" />
            <div className="absolute inset-x-0 top-8 sm:top-[10%] md:top-[14%] flex flex-col items-center text-center px-4 sm:px-6">
              <h1 className="flex flex-col items-center">
                <img
                  src={logoAsset.url}
                  alt="kupish.si"
                  width={544}
                  height={144}
                  fetchPriority="high"
                  decoding="async"
                  className="h-12 sm:h-16 md:h-24 lg:h-28 w-auto"
                />
                <span className="font-script mt-2 sm:mt-3 md:mt-4 text-lg sm:text-2xl md:text-4xl text-foreground tracking-tight leading-tight block">
                  Nakupovanje na enem spletnem mestu.
                </span>
                <span className="font-script mt-1 sm:mt-2 md:mt-3 text-lg sm:text-2xl md:text-4xl text-foreground tracking-tight leading-tight block">
                  Novi izdelki različnih ponudnikov in spletnih trgovin.
                </span>
              </h1>
              <div className="mt-3 sm:mt-5 md:mt-6 w-full max-w-[200px] sm:max-w-xs md:max-w-md flex items-center justify-center">
                <div className="flex-1 h-px bg-border" />
                <img
                  src="/favicon.png"
                  alt=""
                  aria-hidden="true"
                  width={20}
                  height={20}
                  decoding="async"
                  className="mx-3 h-5 w-5 shrink-0 rounded-full"
                />
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="mt-4 grid w-full max-w-[320px] grid-cols-1 gap-2 sm:mt-8 sm:max-w-none sm:flex sm:w-auto sm:items-center sm:justify-center sm:gap-3 md:mt-12 md:gap-5">
                <Link to="/izdelki" className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground min-h-11 px-4 py-3 sm:px-5 md:px-7 md:py-3.5 text-xs sm:text-xs md:text-sm tracking-wider uppercase hover:opacity-90 transition shadow-lg">
                  Razišči ponudbo <span aria-hidden>→</span>
                </Link>
                <Link to="/registracija" className="inline-flex items-center justify-center bg-background/90 backdrop-blur-sm text-foreground min-h-11 px-4 py-3 sm:px-5 md:px-7 md:py-3.5 text-xs sm:text-xs md:text-sm uppercase tracking-wider hover:text-accent transition shadow-lg">
                  Odpri svojo trgovino
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10 md:py-20">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-16">
          {[
            { Icon: Sparkles, t: "Novi izdelki preverjenih ponudnikov na enem mestu." },
            { Icon: Wallet, t: "Kupujete neposredno pri prodajalcu, brez posrednikov." },
            { Icon: Gift, t: "Odprite svojo spletno trgovino in začnite prodajati." },

          ].map((v, i) => (
            <div key={i} className="text-center">
              <v.Icon className="mx-auto mb-3 h-8 w-8 text-accent md:mb-5 md:h-10 md:w-10" strokeWidth={1.25} />
              <h2 className="mx-auto max-w-xs font-serif text-lg leading-snug md:text-2xl md:leading-tight">
                {v.t}
              </h2>
            </div>
          ))}
        </div>
      </section>


      <BrandDivider />
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow">Kategorije</div>
          <h2 className="font-serif text-3xl md:text-5xl mt-3">
            Vsak izdelek ima <span>svojo zgodbo</span>
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">Prebrskaj po kategorijah in odkrij svojo.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {topCategories.map((c) => (
            <div
              key={c.slug}
              className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card hover:border-accent transition-colors min-h-[260px] md:min-h-[280px]"
            >
              {/* Whole-card link to the category */}
              <Link
                to="/kategorije/$slug"
                params={{ slug: c.slug }}
                aria-label={c.name}
                className="absolute inset-0 z-10"
              >
                {c.image_url && (
                  <img
                    src={c.image_url}
                    alt=""
                    aria-hidden
                    width={800}
                    height={560}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover opacity-90 md:scale-[1.12]"
                  />
                )}
                {/* Soft wash for readability */}
                <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/45 to-background/85" />
              </Link>

              {/* Title + description at the top, buttons pushed to the bottom */}
              <div className="relative z-20 pointer-events-none px-5 md:px-8 pt-6 md:pt-8 max-w-[86%] md:max-w-[70%]">
                <h3 className="font-serif whitespace-nowrap text-2xl md:text-3xl lg:text-4xl text-foreground">
                  {c.name}
                </h3>
                <p className="mt-2 text-sm md:text-base text-foreground leading-snug line-clamp-2 max-w-[34ch]">
                  {c.description}
                </p>
              </div>

              {/* Bottom: subcategory buttons */}
              <div className="relative z-20 mt-auto p-4 md:p-5 pt-6">
                <div className="flex flex-wrap items-center gap-2">
                  {c.subcategories.map((sub) => (
                    <Link
                      key={sub.slug}
                      to="/kategorije/$slug"
                      params={{ slug: sub.slug }}
                      className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-accent/40 bg-background/90 backdrop-blur-sm px-3 py-2 text-xs text-foreground hover:border-accent hover:text-accent transition-colors md:min-h-0 md:py-1.5"
                    >
                      <sub.Icon className="h-3.5 w-3.5 text-accent" strokeWidth={1.75} />
                      {sub.name}
                    </Link>
                  ))}
                </div>
              </div>

            </div>
          ))}
        </div>
      </section>


      <BrandDivider />

      {/* Kako deluje – za kupce */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow">Za kupce</div>
          <h2 className="font-serif text-3xl md:text-5xl mt-3">
            Kako <span>deluje</span>
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">
            Do izdelka v treh preprostih korakih.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-10 md:gap-12">
          {[
            { Icon: Search, t: "Poišči", d: "Brskaj po kategorijah in odkrij ponudbo preverjenih trgovin." },
            { Icon: ShoppingCart, t: "Naroči", d: "Izberi izdelek in ga varno naroči neposredno pri prodajalcu." },
            { Icon: PackageCheck, t: "Prejmi", d: "Izdelek prejmeš hitro, neposredno od prodajalca." },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <s.Icon className="h-10 w-10 text-accent mb-5 mx-auto" strokeWidth={1.25} />
              <h3 className="font-serif text-2xl mb-3">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
                {s.d}
              </p>
            </div>
          ))}
        </div>
      </section>

      <BrandDivider />

      {/* Izbrano za vas */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="eyebrow">Izbrano za vas</div>
          <h2 className="font-serif text-3xl md:text-5xl mt-3">
            Izbrano <span>za vas</span>
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">
            Skrbno izbrani izdelki naših ponudnikov – po en izdelek iz vsake trgovine.
          </p>
        </div>
        <FeaturedProducts />
        <div className="text-center mt-10">
          <Link
            to="/izbrano"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-accent hover:text-accent/80 transition"
          >
            Razišči vse izbrane izdelke <span aria-hidden>→</span>
          </Link>

        </div>
      </section>

      <BrandDivider />

      {/* Nasveti in navdih — za kupce */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="eyebrow">Nasveti in navdih</div>
          <h2 className="font-serif text-3xl md:text-5xl mt-3">
            Nasveti za <span>pametno nakupovanje</span>
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">
            Praktični vodniki in nasveti za izbiro izdelkov iz ponudbe trgovin na kupish.si.
          </p>
        </div>
        <ArticleCarousel articles={getArticlesBySection("ideje-in-navdih")} />
        <div className="text-center mt-10">
          <Link
            to="/navdih"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-accent hover:text-accent/80 transition"
          >
            Navdih & zgodbe <span aria-hidden>→</span>
          </Link>
        </div>


      </section>


      {/* CTA za prodajalce */}
      <section className="bg-gradient-to-br from-accent/10 via-background to-primary/10 border-y border-accent/30 py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <img
            src="/favicon.png"
            alt="kupish.si"
            width={32}
            height={32}
            className="mx-auto h-8 w-8 mb-5"
          />
          <div className="eyebrow">Za prodajalce</div>
          <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl mt-3 leading-tight">
            Odprite svojo trgovino na <span>kupish.si</span>
          </h2>
          <p className="mt-4 text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
            V nekaj korakih odpri svojo trgovino in začni prodajati.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 md:gap-4 flex-wrap">
            <Link
              to="/registracija"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 md:px-8 md:py-4 text-xs md:text-sm tracking-wider uppercase hover:opacity-90 transition shadow-lg"
            >
              Odpri svojo trgovino <span aria-hidden>→</span>
            </Link>
            <Link
              to="/postani-prodajalec"
              className="inline-flex items-center text-xs md:text-sm uppercase tracking-wider text-foreground hover:text-accent transition border-b border-accent/40 pb-1"
            >
              Več informacij
            </Link>
          </div>
        </div>
      </section>


      {/* Kako deluje – za prodajalce */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow">Za prodajalce</div>
          <h2 className="font-serif text-3xl md:text-5xl mt-3">
            Od ideje do <span>prodaje</span>
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">
            V nekaj korakih odpri svojo trgovino in začni prodajati.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-10 md:gap-12">
          {[
            { Icon: Store, t: "Odpri trgovino", d: "Hitra registracija in postavitev tvojega profila." },
            { Icon: Camera, t: "Dodaj izdelke", d: "Naloži fotografije, opiše izdelke in določi cene." },
            { Icon: Megaphone, t: "Prodajaj & raste", d: "Doseži kupce po vsej Sloveniji in razširi svojo zgodbo." },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <s.Icon className="h-10 w-10 text-accent mb-5 mx-auto" strokeWidth={1.25} />
              <h3 className="font-serif text-2xl mb-3">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
                {s.d}
              </p>
            </div>
          ))}
        </div>
      </section>

      <BrandDivider />

      {/* Vodič za prodajalce – članki */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="eyebrow">Za prodajalce</div>
          <h2 className="font-serif text-3xl md:text-5xl mt-3">
            Vodič za <span>prodajalce</span>
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">
            Nasveti za kvalitetno spletno predstavitev — od blagovne znamke do fotografije in SEO.
          </p>
        </div>
        <ArticleCarousel
          articles={PRESENTATION_SLUGS.map((s) => getArticle(s)).filter(
            (a): a is NonNullable<ReturnType<typeof getArticle>> => Boolean(a),
          )}
        />
        <div className="text-center mt-10">
          <Link
            to="/predstavitev-prodajalca"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-accent hover:text-accent/80 transition"
          >
            Celoten vodič za prodajalce <span aria-hidden>→</span>
          </Link>
        </div>
      </section>
    </>
  );
}
